import React, { useState, useRef, useEffect, useCallback } from 'react';
import { VStack, HStack, Text, Heading, Input, Field } from '@chakra-ui/react';
import { useTranslation } from '../../hooks/useTranslation';
import { render } from '../../utils/templateUtils';
import Button from '../../components/base/Button';
import {
    useSendRegistrationEmailMutation,
    useVerifyRegistrationEmailMutation,
    getHttpStatus,
} from '../../api';
import { useNavigate } from 'react-router-dom';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SEC = 180; // 3 минуты

interface RegistrationConfirmEmailStepProps {
    email: string;
}

function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export const RegistrationConfirmEmailStep: React.FC<RegistrationConfirmEmailStepProps> = ({ email }) => {
    const { t } = useTranslation();
    const [code, setCode] = useState<string[]>(() => Array(CODE_LENGTH).fill(''));
    const [secondsLeft, setSecondsLeft] = useState(RESEND_COOLDOWN_SEC);
    const navigate = useNavigate();
    const [verifyError, setVerifyError] = useState<string | null>(null);
    const [resendError, setResendError] = useState<string | null>(null);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const hasVerifiedCurrentCode = useRef(false);

    const [sendRegistrationEmail, { isLoading: isResendLoading }] = useSendRegistrationEmailMutation();
    const [verifyRegistrationEmail, { isLoading: isVerifyLoading }] = useVerifyRegistrationEmailMutation();

    // Таймер обратного отсчёта
    useEffect(() => {
        if (secondsLeft <= 0) return;
        const timer = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
        return () => clearInterval(timer);
    }, [secondsLeft]);

    // Автоверификация при заполненном коде
    const codeString = code.join('');
    useEffect(() => {
        if (codeString.length !== CODE_LENGTH || hasVerifiedCurrentCode.current || isVerifyLoading) return;
        hasVerifiedCurrentCode.current = true;
        setVerifyError(null);
        verifyRegistrationEmail({ email, code: Number(codeString) })
            .unwrap()
            .then(() => {
                navigate('/registration/form');
            })
            .catch((err: unknown) => {
                const status = getHttpStatus(err);
                if (status === 400) {
                    setVerifyError(t.registration.errors.invalidEmail);
                } else if (status === 401) {
                    setVerifyError(t.registration.errors.wrongCode);
                } else if (status === 429) {
                    setVerifyError(t.registration.errors.rateLimit);
                } else {
                    setVerifyError(t.registration.errors.serverError);
                }
            });
    }, [codeString, isVerifyLoading]);

    const handleCodeChange = useCallback((index: number, value: string) => {
        const digits = value.replace(/\D/g, '');
        if (!digits) {
            setCode((prev) => {
                const next = [...prev];
                next[index] = '';
                hasVerifiedCurrentCode.current = false;
                return next;
            });
            setVerifyError(null);
            return;
        }

        // iOS/Android OTP autofill can inject full code into one input.
        if (digits.length > 1) {
            const normalized = digits.slice(0, CODE_LENGTH).split('');
            const nextCode = Array(CODE_LENGTH).fill('');
            normalized.forEach((char, idx) => {
                nextCode[idx] = char;
            });
            setCode(nextCode);
            setVerifyError(null);
            hasVerifiedCurrentCode.current = false;
            inputRefs.current[Math.min(normalized.length, CODE_LENGTH - 1)]?.focus();
            return;
        }

        const char = digits.slice(-1);
        setCode((prev) => {
            const next = [...prev];
            next[index] = char;
            if (next.join('').length < CODE_LENGTH) {
                hasVerifiedCurrentCode.current = false;
            }
            return next;
        });
        setVerifyError(null);
        if (char && index < CODE_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    }, []);

    const handleCodeKeyDown = useCallback(
        (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Backspace' && !code[index] && index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
        },
        [code],
    );

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
        if (!pasted) return;
        hasVerifiedCurrentCode.current = false;
        setVerifyError(null);
        const newCode = [...Array(CODE_LENGTH).fill('')];
        pasted.split('').forEach((char, i) => {
            newCode[i] = char;
        });
        setCode(newCode);
        const nextIndex = Math.min(pasted.length, CODE_LENGTH - 1);
        inputRefs.current[nextIndex]?.focus();
    }, []);

    const handleRequestAgain = useCallback(() => {
        if (secondsLeft > 0) return;
        setResendError(null);
        sendRegistrationEmail({ email })
            .unwrap()
            .then(() => setSecondsLeft(RESEND_COOLDOWN_SEC))
            .catch((err: unknown) => {
                const status = getHttpStatus(err);
                if (status === 429) {
                    setResendError(t.registration.errors.rateLimit);
                } else {
                    setResendError(t.registration.errors.serverError);
                }
            });
    }, [secondsLeft, email]);

    return (
        <>
            <Heading size="lg" color="gray.800" textAlign="center" w="full">
                {t.registration.confirmTitle}
            </Heading>

            <VStack gap={6} align="center">
                <Text fontSize="md" color="gray.700" textAlign="center">
                    {render(t.registration.confirmMessage, { email })}
                </Text>

                <Field.Root invalid={!!verifyError} alignItems="center">
                    <HStack gap={2} justify="center" wrap="nowrap">
                        {code.map((digit, index) => (
                            <Input
                                key={index}
                                ref={(el) => {
                                    inputRefs.current[index] = el;
                                }}
                                value={digit}
                                onChange={(e) => handleCodeChange(index, e.target.value)}
                                onKeyDown={(e) => handleCodeKeyDown(index, e)}
                                onPaste={handlePaste}
                                maxLength={1}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                                name={index === 0 ? 'one-time-code' : `verify-code-${index}`}
                                textAlign="center"
                                fontSize="xl"
                                fontWeight="bold"
                                w="10"
                                h="10"
                                borderRadius="0"
                                colorPalette={verifyError ? 'red' : 'gray'}
                                disabled={isVerifyLoading}
                            />
                        ))}
                    </HStack>
                    {verifyError && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={2} textAlign="center">
                            {verifyError}
                        </Field.ErrorText>
                    )}
                </Field.Root>

                <VStack gap={1} align="center">
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                        {t.registration.confirmSpamHint}
                    </Text>
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                        {render(t.registration.confirmRequestAgainIn, {
                            time: secondsLeft > 0 ? formatTime(secondsLeft) : '0:00',
                        })}
                    </Text>
                </VStack>

                {resendError && (
                    <Text fontSize="sm" color="red.500" textAlign="center">
                        {resendError}
                    </Text>
                )}
                <Button
                    type="button"
                    buttonType="secondary"
                    size="lg"
                    w="full"
                    disabled={secondsLeft > 0 || isResendLoading}
                    isLoading={isResendLoading}
                    onClick={handleRequestAgain}
                >
                    {t.registration.confirmRequestAgainButton}
                </Button>
            </VStack>
        </>
    );
};
